// Constants and helpers for the revamped Organizations screen (ADM-6 / ADM-11),
// backed by the v3 organization endpoints.

// Organization status returned by the v3 API. Only these two statuses exist —
// there is no org-level "invited" state.
export const ORG_STATUS = {
  ACTIVE: "ACTIVE",
  DEACTIVATED: "DEACTIVATED",
} as const;

export type OrgStatus = (typeof ORG_STATUS)[keyof typeof ORG_STATUS];

// Maps an organization status to its pill label, pill colors and the wording used
// before the status date ("Active since {date}" / "Deactivated on {date}").
export const ORG_STATUS_DISPLAY: Record<
  string,
  { label: string; pillClass: string; datePrefix: string }
> = {
  [ORG_STATUS.ACTIVE]: {
    label: "Active",
    pillClass: "bg-green-500/10 text-green-500",
    datePrefix: "since",
  },
  [ORG_STATUS.DEACTIVATED]: {
    label: "Deactivated",
    pillClass: "bg-red-500/10 text-red-500",
    datePrefix: "on",
  },
};

// Only these roles may view the screen (mirrors the v3 API authorization).
// FINLEY_ORG_ADMIN stays on the v1 organization screens for now.
export const ORGANIZATIONS_VIEW_ROLES = [
  "SUPER_ADMIN",
  "FINLEY_ADMIN",
  "FINANCIAL_COACH",
] as const;

// Coach is read-only: create and deactivate are limited to the admin roles
// (the server returns 403 for Coach regardless).
export const ORGANIZATIONS_MANAGE_ROLES = ["SUPER_ADMIN", "FINLEY_ADMIN"] as const;

// Role wording for the top-level page title, "Welcome back, {Organization} {role}"
// (e.g. "Welcome back, Finley Admin").
export const ORGANIZATION_ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  FINLEY_ADMIN: "Admin",
  FINANCIAL_COACH: "Coach",
};

export const canViewOrganizations = (role?: string | null): boolean =>
  !!role &&
  ORGANIZATIONS_VIEW_ROLES.includes(role as (typeof ORGANIZATIONS_VIEW_ROLES)[number]);

export const canManageOrganizations = (role?: string | null): boolean =>
  !!role &&
  ORGANIZATIONS_MANAGE_ROLES.includes(role as (typeof ORGANIZATIONS_MANAGE_ROLES)[number]);

// Only Super Admin may reveal or rotate an org's OAuth client credentials
// (mirrors the API; the server returns 403 for every other role, including
// Finley Admin, who can otherwise rename and deactivate).
export const ORGANIZATIONS_CREDENTIALS_ROLES = ["SUPER_ADMIN"] as const;

export const canManageOrganizationCredentials = (role?: string | null): boolean =>
  !!role &&
  ORGANIZATIONS_CREDENTIALS_ROLES.includes(
    role as (typeof ORGANIZATIONS_CREDENTIALS_ROLES)[number]
  );

// The partner docs ask for the client secret to be rotated every 90 days; the
// credentials dialog flags a secret older than this (or never rotated).
export const CREDENTIALS_ROTATION_DAYS = 90;

export const ORGANIZATIONS_PER_PAGE = 10;

// Rename modal (ADM-12): hard limit enforced by the input and shown in the counter.
export const ORG_NAME_MAX_LENGTH = 87;
